import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { useLocalStorage } from 'react-use'

import { LOCALE_STORAGE_KEYS } from 'common/constants/localeStorageConstants'

import {
  DEFAULT_OFFSET,
  DRAG_THRESHOLD_PX,
  LAUNCHER_SIZE,
  PHONE_SHEET_QUERY,
  clampOffset,
  fitPanel,
  isOffset,
} from './widgetPosition'
import type { Offset, Size } from './widgetPosition'

type DragStart = { pointerId: number; x: number; y: number; from: Offset }

const viewportSize = (): Size => ({ width: window.innerWidth, height: window.innerHeight })

const useViewport = (): Size => {
  const [size, setSize] = useState(viewportSize)
  useEffect(() => {
    const onResize = () => setSize(viewportSize())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return size
}

// The open panel is a fixed full-screen sheet on a phone, so it has nowhere to be dragged.
const isPhoneSheet = () => window.matchMedia?.(PHONE_SHEET_QUERY).matches ?? false

const capture = (handle: HTMLElement, pointerId: number) => {
  try {
    handle.setPointerCapture?.(pointerId)
  } catch {
    // An already-released pointer cannot be captured; window listeners still end the drag.
  }
}

// Drags the widget by its bottom-right offset, clamped to the window and remembered per browser.
export const useWidgetDrag = (isOpen: boolean) => {
  const viewport = useViewport()
  const panelSize = fitPanel(viewport)
  const size = isOpen ? panelSize : LAUNCHER_SIZE
  const [stored, setStored] = useLocalStorage<Offset>(
    LOCALE_STORAGE_KEYS.TEAM_CHAT_POSITION,
    DEFAULT_OFFSET,
  )
  const [live, setLive] = useState<Offset | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const stopDrag = useRef<(() => void) | null>(null)
  const dragged = useRef(false)
  const clearDragged = useRef<ReturnType<typeof setTimeout> | null>(null)
  const base = isOffset(stored) ? stored : DEFAULT_OFFSET
  const offset = clampOffset(live ?? base, size, viewport)
  const offsetRef = useRef(offset)
  const sizeRef = useRef(size)
  const viewportRef = useRef(viewport)
  useLayoutEffect(() => {
    offsetRef.current = offset
    sizeRef.current = size
    viewportRef.current = viewport
  })

  // The flag only covers the click of this same pointer sequence, which fires right after pointerup.
  const markDragged = (moved: boolean) => {
    if (clearDragged.current) clearTimeout(clearDragged.current)
    dragged.current = moved
    clearDragged.current = moved ? setTimeout(() => (dragged.current = false), 0) : null
  }

  useEffect(
    () => () => {
      stopDrag.current?.()
      if (clearDragged.current) clearTimeout(clearDragged.current)
    },
    [],
  )

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0 || stopDrag.current) return
    if (isOpen && isPhoneSheet()) return
    markDragged(false)
    const handle = e.currentTarget
    const origin: DragStart = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      from: offsetRef.current,
    }
    const drag = { to: origin.from, moved: false }
    const isOwn = (ev: Event) => (ev as PointerEvent).pointerId === origin.pointerId

    const onMove = (ev: PointerEvent) => {
      if (!isOwn(ev)) return
      const dx = ev.clientX - origin.x
      const dy = ev.clientY - origin.y
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD_PX) return
      drag.to = { right: origin.from.right - dx, bottom: origin.from.bottom - dy }
      drag.moved = true
      setIsDragging(true)
      setLive(drag.to)
    }
    const stop = () => {
      stopDrag.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onEnd)
      window.removeEventListener('pointercancel', onEnd)
      handle.removeEventListener('lostpointercapture', onEnd)
    }
    // Ends only the drag of the pointer that started it; losing capture (an iframe) also ends it.
    function onEnd(ev: Event) {
      if (!isOwn(ev)) return
      stop()
      markDragged(drag.moved)
      if (!drag.moved) return
      // The last pointer position, clamped where it is drawn; a render may not have caught up yet.
      setStored(clampOffset(drag.to, sizeRef.current, viewportRef.current))
      setLive(null)
      setIsDragging(false)
    }

    stopDrag.current = stop
    capture(handle, e.pointerId)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onEnd)
    window.addEventListener('pointercancel', onEnd)
    handle.addEventListener('lostpointercapture', onEnd)
  }

  // A drag that ends over the launcher must not also open it.
  const consumeDrag = () => {
    const was = dragged.current
    markDragged(false)
    return was
  }

  return { offset, panelSize, isDragging, onPointerDown, consumeDrag }
}
